import React, { useState, createContext, useCallback } from "react";
import { sortableColumnApi } from "../api";
import { showToast } from "../../components/Toast";
import { useUser } from "../hooks";
import { localStorageItemNames } from "../constants";
import { DEALEAR_DEFAULT } from "../constants/dealer-name";

/**
 * Sortable Column context
 */
export const SortableColumnContext = createContext();

/**
 * Sortable Column data provider
 *
 * Sort records are fetched for the current user/company so Save Sort can detect
 * an existing configuration from another page.
 */
export const SortableColumnProvider = ({ children }) => {
  const [sortableColumnData, setSortableColumnData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { setColumnChange, setPopupBlock } = useUser();

  const [defaultColDef, setColumnDefs] = useState([]);

  const getSortableColumnApi = async (values) => {
    setLoading(true);
    setColumnChange(false);
    setPopupBlock(false);
    const recordScope = { ...values?.filter };
    delete recordScope.pageName;
    const response = await sortableColumnApi.getSortableColumn({
      limit: values?.limit ? values.limit : 200,
      offset: values?.offset ? values.offset : 0,
      ...values,
      filter: {
        ...recordScope,
      },
      sorting: {
        fieldName: "",
        direction: "",
      },
    });

    if (response?.error) {
      setLoading(false);
      return true;
    }

    if (response?.data?.data) {
      setSortableColumnData(response.data.data);
      setLoading(false);
    } else {
      setSortableColumnData([]);
      setLoading(false);
    }

    return response.data;
  };

  const updateViewSortableColumn = async (values) => {
    setLoading(true);
    const response = await sortableColumnApi.editSortableColumn({ ...values });
    if (response?.error) {
      setLoading(false);
    }

    if (response?.data?.data) {
      await getSortableColumnApi({
        filter: {
          userId: values?.userId,
          pageName: values?.pageName,
          companyId: localStorage.getItem(localStorageItemNames.companyId),
          dealerId: DEALEAR_DEFAULT.dealerId,
        },
      });
      showToast("Sorting Column updated successfully!");
    } else {
      setLoading(false);
      showToast("Something went wrong Try again!");
    }

    return response.data;
  };

  const createSortableColumn = async (values) => {
    setLoading(true);
    const response = await sortableColumnApi.createSortableColumn(values);

    if (response?.error) {
      setLoading(false);
    }

    if (response?.data?.succeeded) {
      const userId = localStorage.getItem(localStorageItemNames.userID);
      await getSortableColumnApi({
        filter: {
          userId: userId,
          pageName: values?.pageName,
          companyId: localStorage.getItem(localStorageItemNames.companyId),
          dealerId: DEALEAR_DEFAULT.dealerId,
        },
      });
      setLoading(false);
      showToast("Sorting Column saved successfully!");
    } else {
      showToast("Something went wrong Try again!");
      setLoading(false);
    }
    return response.data;
  };

  const deleteSortingColumnByIdApi = async (value) => {
    setLoading(true);
    const response = await sortableColumnApi.deleteSortableColumn(value?.id);

    if (response?.error) {
      setLoading(false);
    }

    if (response?.data) {
      const userId = localStorage.getItem(localStorageItemNames.userID);
      await getSortableColumnApi({
        filter: {
          userId: userId,
          pageName: value?.pageName,
          companyId: localStorage.getItem(localStorageItemNames.companyId),
          dealerId: DEALEAR_DEFAULT.dealerId,
        },
      });
      setLoading(false);
    } else {
      setLoading(false);
    }

    return response.data;
  };

  const contextValue = {
    sortableColumnData,
    setSortableColumnData,
    loading,
    setLoading,
    getSortableColumnApi: useCallback((request) => {
      return getSortableColumnApi(request);
    }, []),
    updateViewSortableColumn: useCallback((request) => {
      return updateViewSortableColumn(request);
    }, []),
    createSortableColumn: useCallback((request) => {
      return createSortableColumn(request);
    }),
    deleteSortingColumnByIdApi: useCallback((request) => {
      deleteSortingColumnByIdApi(request);
    }),
  };

  return (
    <SortableColumnContext.Provider value={contextValue}>
      {children}
    </SortableColumnContext.Provider>
  );
};
